import { supabase } from '../supabaseClient';
import {
  EMPTY_SLIDE_LAYOUT,
  parseSlideLayoutJson,
  type ProgramSlideLayoutV1,
} from '../types/programContentSlideCanvas';
import {
  clampLandingIntroHeightRatio,
  emptyContentForKind,
  LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
  parseIntroBlockContent,
  parseIntroSectionKind,
  type LandingIntroBlockContent,
  type LandingIntroDraft,
  type LandingIntroSection,
  type LandingIntroSectionKind,
} from '../types/landingIntro';

const INTRO_CACHE_KEY = 'curriculum-landing-intro.v5';

interface CachedIntro {
  data: LandingIntroSection[];
  /** 행 메타 지문 */
  version: string;
  lastUpdated: string;
}

interface IntroVersionRow {
  id: string;
  updated_at: string;
  is_active: boolean;
  sort_order: number;
  height_ratio: number;
  section_kind: string;
}

function mapRow(row: Record<string, unknown>): LandingIntroSection {
  const section_kind = parseIntroSectionKind(row.section_kind);
  return {
    id: String(row.id),
    sort_order: Number(row.sort_order ?? 0),
    title: '',
    section_kind,
    layout_json: parseSlideLayoutJson(row.layout_json),
    content_json: parseIntroBlockContent(section_kind, row.content_json),
    height_ratio: clampLandingIntroHeightRatio(
      row.height_ratio ?? LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
    ),
    is_active: Boolean(row.is_active),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

export function buildIntroCacheVersion(rows: IntroVersionRow[]): string {
  return rows
    .map(
      (r) =>
        `${r.id}:${r.sort_order}:${r.is_active ? 1 : 0}:${r.section_kind}:${clampLandingIntroHeightRatio(r.height_ratio)}:${r.updated_at}`,
    )
    .join('|');
}

function maxUpdatedAt(rows: { updated_at: string }[]): string {
  return rows.reduce((max, r) => (r.updated_at > max ? r.updated_at : max), rows[0]?.updated_at ?? '');
}

export function loadCachedIntro(): CachedIntro | null {
  try {
    const raw = localStorage.getItem(INTRO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedIntro;
    if (!parsed?.version || !Array.isArray(parsed.data)) return null;
    return {
      ...parsed,
      data: parsed.data.map((item) => {
        const section_kind = parseIntroSectionKind(item.section_kind);
        return {
          ...item,
          title: '',
          section_kind,
          layout_json: parseSlideLayoutJson(item.layout_json),
          content_json: parseIntroBlockContent(section_kind, item.content_json),
          height_ratio: clampLandingIntroHeightRatio(
            item.height_ratio ?? LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
          ),
        };
      }),
    };
  } catch {
    return null;
  }
}

export function saveIntroCache(data: LandingIntroSection[], version?: string): void {
  try {
    const resolvedVersion =
      version ??
      buildIntroCacheVersion(
        data.map((s) => ({
          id: s.id,
          updated_at: s.updated_at,
          is_active: s.is_active,
          sort_order: s.sort_order,
          height_ratio: s.height_ratio,
          section_kind: s.section_kind,
        })),
      );
    const cached: CachedIntro = {
      data,
      version: resolvedVersion,
      lastUpdated: maxUpdatedAt(data) || new Date().toISOString(),
    };
    localStorage.setItem(INTRO_CACHE_KEY, JSON.stringify(cached));
  } catch {
    /* ignore */
  }
}

export function clearIntroCache(): void {
  try {
    localStorage.removeItem(INTRO_CACHE_KEY);
    localStorage.removeItem('curriculum-landing-intro.v4');
    localStorage.removeItem('curriculum-landing-intro.v3');
  } catch {
    /* ignore */
  }
}

export async function fetchIntroCacheVersion(): Promise<string | null> {
  const { data, error } = await supabase
    .from('landing_page_intro')
    .select('id, updated_at, is_active, sort_order, height_ratio, section_kind')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return buildIntroCacheVersion((data ?? []) as IntroVersionRow[]);
}

/** 공개 랜딩용 — 활성 섹션 전체(sort_order 순, 종류 혼합) */
export async function getActiveIntroSections(): Promise<LandingIntroSection[]> {
  const { data, error } = await supabase
    .from('landing_page_intro')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
}

export async function getActiveIntroSectionsWithCache(): Promise<{
  sections: LandingIntroSection[];
  fromCache: boolean;
}> {
  const cached = loadCachedIntro();

  if (cached?.version) {
    try {
      const remoteVersion = await fetchIntroCacheVersion();
      if (remoteVersion != null && remoteVersion === cached.version) {
        return {
          sections: cached.data.filter((x) => x.is_active),
          fromCache: true,
        };
      }
    } catch {
      /* fall through */
    }
  }

  const { data, error } = await supabase
    .from('landing_page_intro')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  const all = (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
  const version = buildIntroCacheVersion(
    all.map((s) => ({
      id: s.id,
      updated_at: s.updated_at,
      is_active: s.is_active,
      sort_order: s.sort_order,
      height_ratio: s.height_ratio,
      section_kind: s.section_kind,
    })),
  );
  saveIntroCache(all, version);
  return {
    sections: all.filter((s) => s.is_active),
    fromCache: false,
  };
}

/** 관리자 CMS용 — 전체 */
export async function getAllIntroSections(): Promise<LandingIntroSection[]> {
  const { data, error } = await supabase
    .from('landing_page_intro')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
}

export async function createIntroSection(input: {
  section_kind: LandingIntroSectionKind;
  layout_json: ProgramSlideLayoutV1;
  content_json: LandingIntroBlockContent;
  height_ratio: number;
  sort_order: number;
  is_active?: boolean;
}): Promise<LandingIntroSection> {
  const { data, error } = await supabase
    .from('landing_page_intro')
    .insert({
      title: '',
      section_kind: input.section_kind,
      layout_json: input.layout_json,
      content_json: input.content_json ?? emptyContentForKind(input.section_kind),
      height_ratio: clampLandingIntroHeightRatio(input.height_ratio),
      sort_order: input.sort_order,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  clearIntroCache();
  return mapRow(data as Record<string, unknown>);
}

export async function updateIntroSection(
  id: string,
  updates: Partial<
    Pick<
      LandingIntroSection,
      'layout_json' | 'content_json' | 'height_ratio' | 'sort_order' | 'is_active' | 'section_kind'
    >
  >,
): Promise<LandingIntroSection> {
  const payload: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
    title: '',
  };
  if (updates.layout_json !== undefined) payload.layout_json = updates.layout_json;
  if (updates.content_json !== undefined) payload.content_json = updates.content_json;
  if (updates.section_kind !== undefined) payload.section_kind = updates.section_kind;
  if (updates.height_ratio !== undefined) {
    payload.height_ratio = clampLandingIntroHeightRatio(updates.height_ratio);
  }
  if (updates.sort_order !== undefined) payload.sort_order = updates.sort_order;
  if (updates.is_active !== undefined) payload.is_active = updates.is_active;

  const { data, error } = await supabase
    .from('landing_page_intro')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  clearIntroCache();
  return mapRow(data as Record<string, unknown>);
}

export async function deleteIntroSection(id: string): Promise<void> {
  const { error } = await supabase.from('landing_page_intro').delete().eq('id', id);
  if (error) throw error;
  clearIntroCache();
}

/** 드래프트 목록을 순서·내용 기준으로 일괄 저장 */
export async function saveIntroDrafts(
  drafts: LandingIntroDraft[],
  deletedIds: string[],
): Promise<LandingIntroSection[]> {
  for (const id of deletedIds) {
    await deleteIntroSection(id);
  }

  for (let i = 0; i < drafts.length; i += 1) {
    const draft = drafts[i];
    const sort_order = i + 1;
    const layout_json = draft.layout_json ?? { ...EMPTY_SLIDE_LAYOUT };
    const height_ratio = clampLandingIntroHeightRatio(draft.height_ratio);
    const content_json =
      draft.content_json ?? emptyContentForKind(draft.section_kind ?? 'slide');

    if (draft.isNew) {
      await createIntroSection({
        section_kind: draft.section_kind,
        layout_json,
        content_json,
        height_ratio,
        sort_order,
        is_active: draft.is_active,
      });
    } else {
      await updateIntroSection(draft.id, {
        section_kind: draft.section_kind,
        layout_json,
        content_json,
        height_ratio,
        sort_order,
        is_active: draft.is_active,
      });
    }
  }

  return getAllIntroSections();
}
